import React from "react";
import {
  AbsoluteFill,
  Easing,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COPY } from "../../b2c/copy";
import { LOOK } from "../../b2c/look";
import { StoreBadge } from "../../b2c/ui/bits";
import { Words } from "../../b2c/ui/Words";
import { FrakLogo } from "../../components/FrakLogo";

/** Logical stage of every reel (9:16, rendered 1:1). */
export const REEL = { width: 1080, height: 1920, fps: 30 } as const;

type Props = {
  /**
   * Caption at the top, on screen from frame 0 (the reel's cover). Omit
   * it when the scene draws its own title.
   */
  hook?: string;
  /** The hook leaves on this frame; the scene handles its own exit. */
  exitAt: number;
  /** End card: enters on `at`. */
  end: { at: number; line: string; sub: string };
  children: React.ReactNode;
};

/**
 * What every Frak reel shares: the blue background, the hook caption at
 * the top of the Reels-safe band, and the end card (logo, line, sub,
 * stores). The scene goes in between.
 */
export const ReelFrame: React.FC<Props> = ({ hook, exitAt, end, children }) => {
  const frame = useCurrentFrame();
  const out = interpolate(frame, [exitAt, exitAt + 12], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.in(Easing.cubic),
  });

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(120% 90% at 50% 0%, ${LOOK.blue} 55%, ${LOOK.blueDeep} 100%)`,
      }}
    >
      {hook ? (
      <div
        style={{
          position: "absolute",
          top: 230,
          left: 110,
          right: 150,
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: 76,
          lineHeight: 1.05,
          letterSpacing: -2.5,
          color: LOOK.white,
          textAlign: "center",
          textWrap: "balance",
          opacity: 1 - out,
          translate: `0 ${-out * 60}px`,
        }}
      >
        {hook}
      </div>
      ) : null}
      {children}
      <EndCard {...end} />
    </AbsoluteFill>
  );
};

const EndCard: React.FC<Props["end"]> = ({ at, line, sub }) => {
  const frame = useCurrentFrame() - at;
  const { fps } = useVideoConfig();
  if (frame < 0) return null;
  const logo = spring({ frame, fps, config: { damping: 12, stiffness: 200, mass: 0.7 } });
  const subIn = spring({ frame: frame - 24, fps, config: { damping: 18, stiffness: 180 } });
  const badges = spring({ frame: frame - 32, fps, config: { damping: 14, stiffness: 200, mass: 0.7 } });

  return (
    <AbsoluteFill
      style={{
        // Centred in the band the Reels UI leaves free.
        padding: "220px 110px 440px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div style={{ scale: logo, marginBottom: 56 }}>
        {/* The shipped SVG is brand blue; flip it to white on blue. */}
        <FrakLogo width={260} style={{ filter: "brightness(0) invert(1)" }} />
      </div>
      <Words
        text={line}
        // Words reads the composition frame, not this card's local one.
        start={at + 6}
        stagger={3}
        fontSize={92}
        color={LOOK.white}
        style={{ maxWidth: 860 }}
      />
      <div
        style={{
          marginTop: 40,
          fontFamily: LOOK.body,
          fontWeight: LOOK.medium,
          fontSize: 40,
          color: LOOK.sky,
          textAlign: "center",
          opacity: subIn,
          translate: `0 ${(1 - subIn) * 20}px`,
        }}
      >
        {sub}
      </div>
      <div
        style={{
          marginTop: 64,
          display: "flex",
          gap: 24,
          opacity: badges,
          translate: `0 ${(1 - badges) * 60}px`,
        }}
      >
        <StoreBadge store="apple" small={COPY.fr.cta.appStoreSmall} name={COPY.fr.cta.appStore} />
        <StoreBadge store="google" small={COPY.fr.cta.playSmall} name={COPY.fr.cta.play} />
      </div>
    </AbsoluteFill>
  );
};
